import { type ReactNode } from "react";
import { cn } from "@cieda/djayma-ui";
import { TrendingUp, TrendingDown } from "lucide-react";

// ---------------------------------------------------------------------------
// FCFA currency formatter (Senegalese locale: space-separated thousands)
// ---------------------------------------------------------------------------

function formatCurrency(value: number): string {
  return `${new Intl.NumberFormat("fr-SN").format(value)} FCFA`;
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat("fr-SN").format(value);
}

function formatPercent(value: number): string {
  return `${new Intl.NumberFormat("fr-SN", { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(value)}%`;
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface KPITrend {
  readonly value: number;
  readonly direction: "up" | "down";
}

export interface KPICardProps {
  readonly label: string;
  readonly value: number | string;
  readonly trend?: KPITrend;
  readonly icon?: ReactNode;
  readonly format?: "currency" | "number" | "percent";
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function KPICard({ label, value, trend, icon, format }: KPICardProps) {
  const formattedValue =
    typeof value === "string"
      ? value
      : format === "currency"
        ? formatCurrency(value)
        : format === "percent"
          ? formatPercent(value)
          : formatNumber(value);

  return (
    <div className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm transition-shadow hover:shadow-md">
      <div className="flex items-start justify-between">
        <p className="text-sm font-medium text-stone-500">{label}</p>
        {icon && (
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-amber-50 text-amber-600">
            {icon}
          </div>
        )}
      </div>

      <p className="mt-2 font-display text-2xl font-semibold text-stone-900">
        {formattedValue}
      </p>

      {trend && (
        <div
          className={cn(
            "mt-2 inline-flex items-center gap-1 text-xs font-medium",
            trend.direction === "up" ? "text-emerald-600" : "text-red-600",
          )}
        >
          {trend.direction === "up" ? (
            <TrendingUp className="h-3.5 w-3.5" />
          ) : (
            <TrendingDown className="h-3.5 w-3.5" />
          )}
          <span data-testid="trend-value">
            {trend.direction === "up" ? "+" : "-"}
            {formatPercent(Math.abs(trend.value))}
          </span>
        </div>
      )}
    </div>
  );
}
